<template>
    <div class="stl-run-wrap">
        <div class="stl-run-scroll">

            <!-- 스테퍼 -->
            <div class="stl-stepper">
                <span class="stl-step" :class="stepClass(1)"><span class="c">{{ stepMark(1) }}</span><span class="s">기준 확인</span></span>
                <span class="stl-conn" :class="{ done: stepDone(1) }"></span>
                <span class="stl-step" :class="stepClass(2)"><span class="c">{{ stepMark(2) }}</span><span class="s">총요금 입력</span></span>
                <span class="stl-conn" :class="{ done: stepDone(2) }"></span>
                <span class="stl-step" :class="stepClass(3)"><span class="c">{{ stepMark(3) }}</span><span class="s">정산(배분)</span></span>
                <span class="stl-conn" :class="{ done: stepDone(3) }"></span>
                <span class="stl-step" :class="stepClass(4)"><span class="c">{{ stepMark(4) }}</span><span class="s">확정</span></span>
            </div>

            <div class="stl-run-grid">

                <div class="stl-flow">

                    <!-- STEP 1: 기준 확인 (항상 완료) -->
                    <div class="stl-run-card stl-run-card--done">
                        <div class="stl-run-done-row">
                            <span class="stl-run-num stl-run-num--done">✓</span>
                            <span class="stl-run-done-title">기준 확인</span>
                            <span class="stl-run-done-val">{{ ym }} · {{ energyLabel }}</span>
                            <span v-if="billingPeriodText" class="stl-run-done-sub">{{ billingPeriodText }}</span>
                        </div>
                    </div>

                    <!-- STEP 2: 총요금 -->
                    <div class="stl-run-card" :class="flowCardClass(2)">
                        <!-- 완료 한 줄 -->
                        <template v-if="stepMode(2) === 'done'">
                            <div class="stl-run-done-row">
                                <span class="stl-run-num stl-run-num--done">✓</span>
                                <span class="stl-run-done-title">총요금</span>
                                <span class="stl-run-done-val">{{ kDisplay.primary }}</span>
                                <span v-if="kDisplay.secondary" class="stl-run-done-sub">{{ kDisplay.secondary }}원</span>
                                <button v-if="!isFinalOrCancelled" type="button" class="stl-flow-edit" @click="expandedStep = 2">수정</button>
                            </div>
                        </template>
                        <!-- 진행 중 / 수정 모드 -->
                        <template v-else-if="stepMode(2) === 'active'">
                            <div class="stl-run-card-head">
                                <span class="stl-run-num stl-run-num--active">2</span>
                                <span class="stl-run-card-title">총요금</span>
                                <span class="stl-run-card-sub">{{ energySource === 'Elec' ? '한전 고지액' : '고지서 총액' }}</span>
                                <button v-if="expandedStep === 2" type="button" class="stl-flow-edit" style="margin-left:auto;" @click="expandedStep = null">접기</button>
                            </div>
                            <div class="stl-run-card-body">
                                <KepcoBillPanel
                                    v-if="energySource === 'Elec'"
                                    v-model="kepco"
                                    :site="site"
                                    :reg-ym="regYm"
                                    :disabled="isFinalOrCancelled"
                                    @calculate="doPreview"
                                />
                                <div v-if="energySource === 'Elec'" class="stl-flow-cta-row" style="margin-top: 12px;">
                                    <button
                                        type="button"
                                        class="stl-btn stl-btn-primary stl-flow-go"
                                        :disabled="!kReady || busy || isFinalOrCancelled"
                                        @click="doPreview"
                                    >
                                        <b-spinner v-if="busy && loadingResult" small />
                                        <span v-else>배분 계산 →</span>
                                    </button>
                                </div>
                                <div v-else>
                                    <div class="stl-field">
                                        <label>총요금 (원)</label>
                                        <div class="stl-flow-inline">
                                            <AmountKinput v-model="kepco.kepcoTotalAmount" :disabled="isFinalOrCancelled" />
                                            <button
                                                type="button"
                                                class="stl-btn stl-btn-primary stl-flow-go"
                                                :disabled="!kReady || busy || isFinalOrCancelled"
                                                @click="doPreview"
                                            >
                                                <b-spinner v-if="busy && loadingResult" small />
                                                <span v-else>배분 계산 →</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <!-- 미래 -->
                        <template v-else>
                            <div class="stl-run-done-row">
                                <span class="stl-run-num">2</span>
                                <span class="stl-run-done-title">총요금</span>
                            </div>
                        </template>
                    </div>

                    <!-- STEP 3: 정산(배분) -->
                    <div class="stl-run-card" :class="flowCardClass(3)">
                        <!-- 완료 한 줄 -->
                        <template v-if="stepMode(3) === 'done'">
                            <div class="stl-run-done-row">
                                <span class="stl-run-num stl-run-num--done">✓</span>
                                <span class="stl-run-done-title">정산(배분)</span>
                                <span class="stl-run-done-val">{{ result && result.details ? result.details.length + '세대' : '완료' }}</span>
                                <span v-if="result" class="stl-run-done-sub">{{ L.remainder }} {{ fmtWon(residual) }}원</span>
                                <button v-if="!isFinalOrCancelled" type="button" class="stl-flow-edit" @click="expandedStep = 3">보기</button>
                            </div>
                        </template>
                        <!-- 미래 -->
                        <template v-else-if="stepMode(3) === 'future'">
                            <div class="stl-run-done-row">
                                <span class="stl-run-num">3</span>
                                <span class="stl-run-done-title">정산(배분)</span>
                            </div>
                        </template>
                        <!-- 진행 중 / 보기 모드 -->
                        <template v-else>
                            <div class="stl-run-card-head">
                                <span class="stl-run-num stl-run-num--active">3</span>
                                <span class="stl-run-card-title">정산(배분)</span>
                                <span class="stl-run-card-sub">전체 {{ energyLabel }} 사용량</span>
                                <span v-if="result && result.details" class="stl-run-card-sub">· {{ result.details.length }}세대</span>
                                <span v-if="displayStatus" :class="statusClass(displayStatus)" style="margin-left:auto;">{{ statusLabel(displayStatus) }}</span>
                                <button v-if="expandedStep === 3" type="button" class="stl-flow-edit" @click="expandedStep = null">접기</button>
                            </div>
                            <div class="stl-run-card-body">
                                <ReconcileSummary :data="usageData" :loading="loadingReconcile" :usage-unit="summaryUsageUnit" />

                                <div v-if="areaError" class="stl-banner stl-banner-danger" style="margin-top: 10px; margin-bottom: 0;">
                                    <i class="fa fa-exclamation-circle"></i>
                                    <span>면적 미입력 세대가 있어 정산할 수 없습니다 — {{ areaError }}
                                        <a href="javascript:void(0)" @click="$emit('open-site-settings')">단지정보 열기</a>
                                    </span>
                                </div>

                                <NegativePoolBanner v-if="result" :warnings="result.warnings" style="margin-top: 10px;" />

                                <div v-if="loadingResult" class="stl-loading">
                                    <b-spinner small variant="secondary" /><span>비율 배분 계산 중...</span>
                                </div>
                                <template v-else-if="result">
                                    <HouseholdResultGrid
                                        :details="result.details"
                                        :adjustable="currentStatus === 'DRAFT'"
                                        :kepco-total-amount="result.kepcoTotalAmount"
                                        :show-area-column="showAreaShare"
                                        @adjust="openAdjustModal"
                                    />
                                </template>

                                <div v-if="canSaveDraft" class="stl-flow-cta-row">
                                    <button
                                        type="button"
                                        class="stl-btn stl-btn-primary stl-flow-go"
                                        :disabled="busy"
                                        @click="doSimulate"
                                    >
                                        <b-spinner v-if="busy" small />
                                        <span v-else>임시 저장 후 확정으로 →</span>
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- STEP 4: 확정 -->
                    <div class="stl-run-card" :class="flowCardClass(4)">
                        <!-- 완료 한 줄 (FINAL / CANCELLED) -->
                        <template v-if="stepMode(4) === 'done'">
                            <div class="stl-run-done-row">
                                <span class="stl-run-num stl-run-num--done">✓</span>
                                <span class="stl-run-done-title">확정</span>
                                <span :class="statusClass(currentStatus)">{{ statusLabel(currentStatus) }}</span>
                            </div>
                        </template>
                        <!-- 미래 -->
                        <template v-else-if="stepMode(4) === 'future'">
                            <div class="stl-run-done-row">
                                <span class="stl-run-num">4</span>
                                <span class="stl-run-done-title">확정</span>
                            </div>
                        </template>
                        <!-- 진행 중 (DRAFT) -->
                        <template v-else>
                            <div class="stl-run-card-head">
                                <span class="stl-run-num stl-run-num--active">4</span>
                                <span class="stl-run-card-title">확정</span>
                            </div>
                            <div class="stl-run-card-body">
                                <div class="stl-check">
                                    <span class="ck" :class="{ idle: !stepDone(2) }">
                                        <i class="stl-tick" :class="{ idle: !stepDone(2) }">{{ stepDone(2) ? '✓' : '○' }}</i>배분 미리보기 완료
                                    </span>
                                    <span class="ck" :class="{ idle: !stepDone(3) }">
                                        <i class="stl-tick" :class="{ idle: !stepDone(3) }">{{ stepDone(3) ? '✓' : '○' }}</i>배분 결과 확인
                                    </span>
                                    <span class="ck" :class="{ idle: !usageOk }">
                                        <i class="stl-tick" :class="{ idle: !usageOk }">{{ usageOk ? '✓' : '○' }}</i>사용량 대조 일치
                                    </span>
                                    <span class="ck" :class="{ idle: currentStatus !== 'DRAFT' }">
                                        <i class="stl-tick" :class="{ idle: currentStatus !== 'DRAFT' }">{{ currentStatus === 'DRAFT' ? '✓' : '○' }}</i>
                                        {{ currentStatus === 'DRAFT' ? '임시 저장됨' : '임시 저장 대기' }}
                                    </span>
                                </div>
                                <div class="stl-flow-cta-row">
                                    <button
                                        type="button"
                                        class="stl-btn stl-btn-final stl-flow-go"
                                        :disabled="busy || currentStatus !== 'DRAFT'"
                                        @click="doConfirm"
                                    >
                                        <b-spinner v-if="busy" small />
                                        <span v-else>🔒 정산 확정</span>
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>

                </div>

            </div>
        </div>

        <div class="stl-run-footer">
            <div class="stl-run-footer-item">
                <span class="stl-run-footer-lbl">총요금</span>
                <span class="stl-run-footer-big">{{ kDisplay.primary }}</span>
                <span v-if="kDisplay.secondary" class="stl-run-footer-sec">{{ kDisplay.secondary }}원</span>
            </div>
            <span class="stl-run-footer-sep" />
            <div class="stl-run-footer-item">
                <span class="stl-run-footer-lbl">{{ L.remainder }}</span>
                <span
                    class="stl-run-footer-big"
                    :class="result ? (residual === 0 ? 'c-ok' : 'c-warn') : ''"
                >
                    {{ result ? fmtWon(residual) + "원" : "—" }}
                </span>
            </div>
            <span class="stl-run-footer-sep" />
            <div class="stl-run-footer-item">
                <span class="stl-run-footer-lbl">상태</span>
                <span class="stl-run-footer-sec" :class="displayStatus ? statusClass(displayStatus) : ''">
                    {{ displayStatus ? statusLabel(displayStatus) : "—" }}
                </span>
            </div>
            <span class="stl-run-footer-sp" />
            <button
                v-if="kReady && !result && !isFinalOrCancelled"
                type="button"
                class="stl-run-cta"
                :disabled="busy"
                @click="doPreview"
            >
                <b-spinner v-if="busy && loadingResult" small />
                <span v-else>배분 계산</span>
            </button>
            <button
                v-else-if="canSaveDraft"
                type="button"
                class="stl-run-cta"
                :disabled="busy"
                @click="doSimulate"
            >
                <b-spinner v-if="busy" small />
                <span v-else>임시 저장</span>
            </button>
            <template v-if="settlement">
                <button
                    type="button"
                    class="stl-btn stl-btn-sm"
                    :disabled="!['FINAL', 'CANCELLED'].includes(currentStatus) || busy"
                    @click="doReclose"
                >
                    재마감
                </button>
                <button
                    type="button"
                    class="stl-btn stl-btn-sm"
                    :disabled="currentStatus !== 'FINAL' || busy"
                    @click="doCancel"
                >
                    취소
                </button>
                <button
                    type="button"
                    class="stl-btn stl-btn-sm stl-btn-danger"
                    :disabled="currentStatus !== 'DRAFT' || busy"
                    @click="doDelete"
                >
                    삭제
                </button>
            </template>
        </div>

        <!-- 확정 전 사용량 대조 모달 -->
        <b-modal
            v-model="confirmReconcileModal.show"
            :title="`확정 전 사용량 대조 · ${energyLabel}`"
            centered
            content-class="stl-settlement-portal"
            @hidden="onConfirmReconcileHidden"
        >
            <div class="settlement-page">
                <div v-if="confirmReconcileModal.loading" class="stl-loading"><b-spinner small variant="secondary" /><span>대조 조회 중...</span></div>
                <template v-else-if="confirmReconcileModal.data">
                    <div style="margin-bottom: 12px;">
                        <span
                            class="stl-chip"
                            :class="confirmReconcileOk ? 'stl-chip-ok' : 'stl-chip-warn'"
                            :title="confirmReconcileTooltip"
                        >
                            관제점 · {{ confirmReconcileOk ? "일치" : "불일치" }}
                        </span>
                    </div>
                    <ReconcileSummary :data="confirmReconcileModal.data" :usage-unit="summaryUsageUnit" />
                    <p v-if="!confirmReconcileOk" class="stl-hint" style="margin-top: 12px; color: #a06d1a;">
                        사용량 대조에 차이가 있습니다. 확정을 계속 진행할까요?
                    </p>
                </template>
                <div v-else class="stl-empty"><p>대조 데이터를 불러오지 못했습니다.</p></div>
            </div>
            <template #modal-footer>
                <button type="button" class="stl-btn" @click="confirmReconcileModal.show = false">취소</button>
                <button type="button" class="stl-btn stl-btn-final" :disabled="busy || confirmReconcileModal.loading" @click="doConfirmFinal">확정</button>
            </template>
        </b-modal>

        <!-- 조정 모달 -->
        <b-modal v-model="adjustModal.show" title="수동 조정" centered content-class="stl-settlement-portal" @ok="submitAdjust">
            <template v-if="adjustModal.row">
                <p style="font-size: 13px;">
                    <strong>{{ adjustModal.row.roomName }}</strong> — {{ L.usageShare }} {{ fmtWon(adjustModal.row.individualCost) }}원 / {{ L.areaShare }}
                    {{ fmtWon(adjustModal.row.allocatedCommonCost) }}원
                </p>
                <div class="stl-field" style="margin-bottom: 10px;">
                    <label>조정 금액 (원, 음수 가능)</label>
                    <input v-model.number="adjustModal.amount" type="number" step="1" class="form-control form-control-sm" />
                </div>
                <div class="stl-field">
                    <label>조정 사유 *</label>
                    <input v-model="adjustModal.reason" type="text" class="form-control form-control-sm" placeholder="예: 검침 오류 보정" />
                </div>
            </template>
        </b-modal>
    </div>
</template>


<script>
import backEndApi from "@api/backEndApi.js";
import KepcoBillPanel from "../components/KepcoBillPanel.vue";
import AmountKinput from "../components/AmountKinput.vue";
import ReconcileSummary from "../components/ReconcileSummary.vue";
import HouseholdResultGrid from "../components/HouseholdResultGrid.vue";
import NegativePoolBanner from "../components/NegativePoolBanner.vue";
import {
    fmtWon,
    fmtUsage,
    fmtWonCompact,
    fmtDate,
    ymToRegYm,
    statusLabel,
    statusClass,
    parseApiError,
    SETTLEMENT_LABELS,
} from "../utils/settlementFormat.js";

export default {
    name: "SettlementRunTab",
    components: { KepcoBillPanel, AmountKinput, ReconcileSummary, HouseholdResultGrid, NegativePoolBanner },
    props: {
        site: { type: Object, required: true },
        ym: { type: String, required: true },
        energySource: { type: String, default: "Elec" },
    },
    data() {
        return {
            kepco: { kepcoSource: "MANUAL", kepcoTotalAmount: null },
            reconcileData: null,
            loadingReconcile: false,
            result: null,
            loadingResult: false,
            settlement: null,
            areaError: null,
            busy: false,
            expandedStep: null,
            adjustModal: { show: false, row: null, amount: 0, reason: "" },
            confirmReconcileModal: { show: false, data: null, loading: false },
        };
    },
    computed: {
        L() {
            return SETTLEMENT_LABELS;
        },
        regYm() {
            return ymToRegYm(this.ym);
        },
        currentStatus() {
            return this.settlement?.status || null;
        },
        displayStatus() {
            if (this.currentStatus) return this.currentStatus;
            if (this.result?.status === "PREVIEW") return "PREVIEW";
            return null;
        },
        isFinalOrCancelled() {
            return ["FINAL", "CANCELLED"].includes(this.currentStatus);
        },
        energyLabel() {
            if (this.energySource === "Gas") return "가스";
            if (this.energySource === "Water") return "수도";
            return "전기";
        },
        showAreaShare() {
            return this.energySource === "Elec";
        },
        kReady() {
            if (this.isFinalOrCancelled) return false;
            return this.kepco.kepcoTotalAmount !== null && this.kepco.kepcoTotalAmount !== "";
        },
        canSaveDraft() {
            return this.kReady && this.result && this.result.status === "PREVIEW";
        },
        billingPeriodText() {
            const src = this.result || this.reconcileData;
            if (!src?.billingStartAt) return null;
            return `${fmtDate(src.billingStartAt)} ~ ${fmtDate(src.billingEndAt)}`;
        },
        usageData() {
            const usageUnit = this.summaryUsageUnit;
            if (this.result?.masterUsageQty != null) {
                return {
                    masterUsageQty: this.result.masterUsageQty,
                    subUsageSumQty: this.result.subUsageSumQty,
                    commonUsageQty: this.result.commonUsageQty,
                    usageUnit,
                };
            }
            if (this.reconcileData) {
                return {
                    ...this.reconcileData,
                    usageUnit: this.reconcileData.usageUnit || usageUnit,
                };
            }
            return null;
        },
        summaryUsageUnit() {
            if (this.result?.details?.[0]?.usageUnit) return this.result.details[0].usageUnit;
            if (this.reconcileData?.usageUnit) return this.reconcileData.usageUnit;
            if (this.result?.usageUnit) return this.result.usageUnit;
            const src = this.result?.energySource || this.reconcileData?.energySource || this.energySource;
            return src === "Gas" ? "Nm3" : src === "Water" ? "m3" : "kWh";
        },
        usageOk() {
            const d = this.usageData;
            if (!d || d.masterUsageQty == null) return false;
            if (this.energySource === "Gas" || this.energySource === "Water") {
                return Math.abs(Number(d.masterUsageQty) - Number(d.subUsageSumQty)) < 0.0001;
            }
            if (d.commonUsageQty == null) return false;
            const diff = Number(d.masterUsageQty) - (Number(d.subUsageSumQty) + Number(d.commonUsageQty));
            return Math.abs(diff) < 0.0001;
        },
        residual() {
            if (this.result?.floorRemainder != null) return Number(this.result.floorRemainder);
            const w = (this.result?.warnings || []).find((x) => x.code === "TOTAL_NE_K");
            return w ? Number(w.floorRemainder ?? w.diff ?? 0) : 0;
        },
        kDisplay() {
            const v = this.result ? this.result.kepcoTotalAmount : this.kepco.kepcoTotalAmount;
            if (v === null || v === undefined || v === "") return { primary: "—", secondary: null };
            return fmtWonCompact(v);
        },
        confirmReconcileOk() {
            if (!this.confirmReconcileModal.data) return true;
            const noCommon = this.energySource === "Gas" || this.energySource === "Water";
            return this.reconcileMatch(this.confirmReconcileModal.data, noCommon);
        },
        confirmReconcileTooltip() {
            const d = this.confirmReconcileModal.data;
            if (!d) return "";
            const unit = this.summaryUsageUnit === "Nm3" ? "Nm³" : "kWh";
            const m = fmtUsage(d.masterUsageQty);
            const s = fmtUsage(d.subUsageSumQty);
            if (this.energySource === "Gas") {
                const diff = fmtUsage(d.diff ?? Number(d.masterUsageQty) - Number(d.subUsageSumQty));
                return `총 사용량 ${m} − 전체 세대 ${s} = ${diff} ${unit}`;
            }
            const c = fmtUsage(d.commonUsageQty);
            const diff = fmtUsage(d.diff ?? Number(d.masterUsageQty) - Number(d.subUsageSumQty) - Number(d.commonUsageQty || 0));
            return `총 사용량 ${m} − (전체 세대 ${s} + 공용부 ${c}) = ${diff} ${unit}`;
        },
    },
    watch: {
        ym: { handler: "onContextChange" },
        energySource: { handler: "onContextChange" },
        currentStatus(val) {
            this.$emit("status-change", val);
        },
    },
    mounted() {
        this.refresh();
    },
    methods: {
        fmtWon,
        fmtUsage,
        statusLabel,
        statusClass,
        /* ---- 스테퍼 상태 ---- */
        stepDone(n) {
            if (n === 1) return true;
            if (n === 2) return !!this.result;
            if (n === 3) return this.currentStatus === "DRAFT" || this.currentStatus === "FINAL";
            if (n === 4) return this.currentStatus === "FINAL";
            return false;
        },
        stepNow(n) {
            if (this.stepDone(n)) return false;
            for (let i = 1; i < n; i++) {
                if (!this.stepDone(i)) return false;
            }
            return true;
        },
        stepClass(n) {
            return { done: this.stepDone(n), now: this.stepNow(n) };
        },
        stepMark(n) {
            return this.stepDone(n) ? "✓" : String(n);
        },
        /* 'done' | 'active' | 'future' */
        stepMode(n) {
            if (this.isFinalOrCancelled) {
                return this.stepDone(n) ? "done" : "future";
            }
            if (this.expandedStep === n) return "active";
            if (n === 3 && this.result && !this.isFinalOrCancelled && (this.canSaveDraft || this.currentStatus === "DRAFT")) {
                return "active";
            }
            if (n === 4 && this.currentStatus === "DRAFT") return "active";
            if (this.stepNow(n)) return "active";
            if (this.stepDone(n)) return "done";
            return "future";
        },
        flowCardClass(n) {
            const mode = this.stepMode(n);
            if (mode === "active") return "stl-run-card--active";
            if (mode === "done") return "stl-run-card--done";
            return "stl-run-card--future";
        },
        reconcileMatch(data, noCommonSplit) {
            if (!data) return true;
            if (data.diff !== null && data.diff !== undefined) return Math.abs(Number(data.diff)) < 0.0001;
            if (noCommonSplit) return Math.abs(Number(data.masterUsageQty) - Number(data.subUsageSumQty)) < 0.0001;
            if (data.commonUsageQty == null) return false;
            return Math.abs(Number(data.masterUsageQty) - Number(data.subUsageSumQty) - Number(data.commonUsageQty)) < 0.0001;
        },
        onContextChange() {
            this.result = null;
            this.areaError = null;
            this.settlement = null;
            this.expandedStep = null;
            this.kepco = { kepcoSource: "MANUAL", kepcoTotalAmount: null };
            this.refresh();
        },
        /* ---- 데이터 로딩 ---- */
        async refresh() {
            await Promise.all([this.loadExisting(), this.loadReconcile()]);
        },
        async loadExisting() {
            this.settlement = null;
            try {
                const { data } = await backEndApi.settlement.getSettlementList({
                    siteIdx: this.site.siteIdx,
                    regYm: this.regYm,
                    energySource: this.energySource,
                });
                const list = Array.isArray(data) ? data : [];
                if (list.length) {
                    this.settlement = list[0];
                    const { data: detail } = await backEndApi.settlement.getSettlement(this.settlement.settlementIdx);
                    this.result = this.normalizeResult(detail);
                    this.kepco = {
                        kepcoSource: detail.kepcoSource || "MANUAL",
                        kepcoTotalAmount: parseFloat(detail.kepcoTotalAmount || 0),
                    };
                    if (this.settlement.status === "DRAFT") this.expandedStep = 4;
                    else if (this.result) this.expandedStep = 3;
                } else {
                    this.kepco = { kepcoSource: "MANUAL", kepcoTotalAmount: null };
                }
            } catch (err) {
                // 목록 조회 실패는 치명적이지 않음
            }
        },
        normalizeResult(detail) {
            return {
                ...detail,
                kepcoTotalAmount: parseFloat(detail.kepcoTotalAmount || 0),
                commonPoolAmount: parseFloat(detail.commonPoolAmount || 0),
                householdSumIndividual: parseFloat(detail.householdSumIndividual || 0),
                details: (detail.details || []).map((d) => ({
                    ...d,
                    exclusiveArea: parseFloat(d.exclusiveArea || 0),
                    usageQty: parseFloat(d.usageQty || 0),
                    individualCost: parseFloat(d.individualCost || 0),
                    allocatedCommonCost: parseFloat(d.allocatedCommonCost || 0),
                    manualAdjustAmount: parseFloat(d.manualAdjustAmount || 0),
                    totalAmount: parseFloat(d.totalAmount || 0),
                })),
            };
        },
        async loadReconcile() {
            this.loadingReconcile = true;
            this.reconcileData = null;
            try {
                const { data } = await backEndApi.settlement.reconcile({
                    siteIdx: this.site.siteIdx,
                    regYm: this.regYm,
                    energySource: this.energySource,
                });
                this.reconcileData = data;
            } catch (err) {
                // 대조 실패는 정보성
            } finally {
                this.loadingReconcile = false;
            }
        },
        handleError(err) {
            const e = parseApiError(err);
            if (e.errcode === "AREA_NOT_FILLED") {
                this.areaError = e.message;
            } else if (e.errcode === "KEPCO_BILL_NOT_FOUND") {
                this.alertNoti("해당 월 한전 고지가 없습니다. 총요금을 수동 입력하세요.");
            } else if (e.errcode === "INVALID_STATUS_TRANSITION") {
                this.alertNoti(e.message);
                this.loadExisting();
            } else {
                this.alertWarning(e.message);
            }
        },
        buildParams() {
            return {
                siteIdx: this.site.siteIdx,
                regYm: this.regYm,
                energySource: this.energySource,
                kepcoSource: this.kepco.kepcoSource,
                kepcoTotalAmount: this.kepco.kepcoSource === "MANUAL" ? this.kepco.kepcoTotalAmount : undefined,
            };
        },
        /* ---- 액션 ---- */
        async doPreview() {
            if (!this.kReady || this.busy) return;
            this.busy = true;
            this.loadingResult = true;
            this.areaError = null;
            try {
                const { data } = await backEndApi.settlement.preview(this.buildParams());
                this.result = this.normalizeResult(data);
                this.expandedStep = 3;
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
                this.loadingResult = false;
            }
        },
        async doSimulate() {
            this.busy = true;
            try {
                const { data } = await backEndApi.settlement.simulate(this.buildParams());
                this.result = this.normalizeResult(data);
                this.settlement = { settlementIdx: data.settlementIdx, status: "DRAFT" };
                this.expandedStep = 4;
                this.alertNoti("임시 저장되었습니다.");
                this.$emit("settled");
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
            }
        },
        async doConfirm() {
            if (this.busy || this.currentStatus !== "DRAFT") return;
            this.confirmReconcileModal = { show: true, data: null, loading: true };
            try {
                const { data } = await backEndApi.settlement.reconcile({
                    siteIdx: this.site.siteIdx,
                    regYm: this.regYm,
                    energySource: this.energySource,
                    includeHouseholds: "0",
                });
                this.confirmReconcileModal.data = data;
            } finally {
                this.confirmReconcileModal.loading = false;
            }
        },
        onConfirmReconcileHidden() {
            this.confirmReconcileModal.loading = false;
            this.confirmReconcileModal.data = null;
        },
        async doConfirmFinal() {
            const ok = await this.alertQuestion("정산을 확정합니다. 확정 후에는 조정이 불가하며, 취소·재마감 절차로만 변경할 수 있습니다. 진행할까요?");
            if (!ok.value) return;
            this.busy = true;
            try {
                await backEndApi.settlement.confirmSettlement(this.settlement.settlementIdx);
                this.confirmReconcileModal.show = false;
                this.alertNoti("확정되었습니다.");
                await this.loadExisting();
                this.$emit("settled");
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
            }
        },
        async doCancel() {
            const ok = await this.alertConfirmWarning("확정된 정산을 취소합니다. 진행할까요?");
            if (!ok.value) return;
            this.busy = true;
            try {
                await backEndApi.settlement.cancelSettlement(this.settlement.settlementIdx);
                this.alertNoti("취소되었습니다.");
                await this.loadExisting();
                this.$emit("settled");
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
            }
        },
        async doReclose() {
            const ok = await this.alertQuestion("정산을 재마감합니다. 임시저장 상태로 되돌아갑니다. 진행할까요?");
            if (!ok.value) return;
            this.busy = true;
            try {
                await backEndApi.settlement.recloseSettlement(this.settlement.settlementIdx);
                this.alertNoti("재마감되었습니다.");
                await this.loadExisting();
                this.$emit("settled");
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
            }
        },
        async doDelete() {
            const ok = await this.alertConfirmWarning("임시 저장된 정산을 삭제합니다. 되돌릴 수 없습니다. 진행할까요?");
            if (!ok.value) return;
            this.busy = true;
            try {
                await backEndApi.settlement.deleteSettlement(this.settlement.settlementIdx);
                this.alertNoti("삭제되었습니다.");
                this.settlement = null;
                this.result = null;
                this.$emit("settled");
            } catch (err) {
                this.handleError(err);
            } finally {
                this.busy = false;
            }
        },
        openAdjustModal(row) {
            this.adjustModal = {
                show: true,
                row,
                amount: Number(row.manualAdjustAmount || 0),
                reason: row.adjustReason || "",
            };
        },
        async submitAdjust(evt) {
            if (!this.adjustModal.reason) {
                evt.preventDefault();
                this.alertNoti("조정 사유를 입력하세요.");
                return;
            }
            try {
                await backEndApi.settlement.adjustDetail(this.settlement.settlementIdx, this.adjustModal.row.sysNodeIdx, {
                    manualAdjustAmount: this.adjustModal.amount,
                    adjustReason: this.adjustModal.reason,
                });
                this.alertNoti("조정이 반영되었습니다.");
                await this.loadExisting();
            } catch (err) {
                this.handleError(err);
            }
        },
    },
};
</script>
